/* Kickers and metadata -------------------------------------------------- */
.kicker {
  font: 500 10px var(--mono);
  letter-spacing: .2em;
  text-transform: uppercase;
  color: var(--olive);
}
.kicker.tight  { letter-spacing: .16em; }
.kicker.wide   { letter-spacing: .22em; }
.kicker.wider  { letter-spacing: .24em; }
.kicker.lemon  { color: var(--lemon); }
.kicker.onDark { color: rgba(255, 214, 51, .8); font-size: 9.5px; letter-spacing: .22em; }

.meta { font: 400 10.5px var(--mono); color: var(--ink-45); }

.ruleRow { display: flex; align-items: baseline; gap: 12px; }
.ruleRow .line { flex: 1; height: 1px; background: var(--rule); }

/* Prose ------------------------------------------------------------------ */
.prose {
  margin: 0 0 18px;
  max-width: var(--measure);
  font: 400 16.5px/1.75 var(--sans);
  color: var(--body);
  text-wrap: pretty;
}
.prose strong { font-weight: 700; color: var(--ink); }
.prose em { font-style: italic; }

.inlineCode {
  font: 500 14.5px var(--mono);
  background: var(--panel);
  color: var(--ink);
  padding: 1px 5px;
  border-radius: 3px;
}

/* Code ------------------------------------------------------------------- */
.codeBlock {
  background: var(--code-bg);
  border-radius: var(--radius-lg);
  overflow: hidden;
  margin: 20px 0;
}
.codeBar {
  padding: 9px 22px;
  border-bottom: 1px solid rgba(255, 255, 255, .09);
  font: 400 10.5px var(--mono);
  letter-spacing: .06em;
  color: var(--code-com);
}
.pre {
  margin: 0;
  padding: 20px 22px;
  overflow-x: auto;
  font: 400 13.5px/1.7 var(--mono);
  color: var(--code-fg);
}
/* A file's lines, numbered like an editor. Prose says "line 2", and error
   messages give line numbers; the student should not have to count. */
.numbered { counter-reset: line; }
.numbered > span::before {
  counter-increment: line; content: counter(line);
  display: inline-block; width: 2ch; margin-right: 2ch; text-align: right;
  color: var(--code-com); opacity: .7; user-select: none;
}
.kw  { color: var(--code-kw); }
.num { color: var(--code-num); }
.com { color: var(--code-com); }
.str { color: var(--code-str); }

/* Directive blocks ------------------------------------------------------- */
.block {
  margin: 24px 0;
  padding: 18px 20px;
  border-radius: var(--radius);
}
.blockLabel {
  font: 600 10.5px var(--mono);
  letter-spacing: .16em;
  text-transform: uppercase;
}
.blockBody { margin-top: 8px; font: 400 14.5px/1.65 var(--sans); }
.blockBody > :last-child { margin-bottom: 0; }
.blockBody .prose { font: inherit; color: inherit; }
.blockBody .prose strong { color: inherit; }
/* Lists inside a block take the block's colours and size, as its paragraphs
   do. Without this, a list in a dark notice kept the page's dark body text
   and was unreadable (found 2026-09-30, first-splash's "Read this first"). */
.blockBody ul, .blockBody ol { font: inherit; color: inherit; }
.blockBody li strong { color: inherit; }
.blockBody .inlineCode { background: var(--block-code); color: inherit; }
.notice .blockBody .inlineCode,
.submit .blockBody .inlineCode { background: var(--invert-code); }

.borderLeft { border-left: 3px solid var(--accent); border-radius: 0 var(--radius) var(--radius) 0; }

.notice { background: var(--invert-bg); color: var(--invert-fg); }
.notice .blockLabel { color: var(--invert-label); }
.check  { background: var(--check-bg); color: var(--body); }
.check  .blockLabel { color: var(--olive-2); }
.try    { background: var(--try-bg); color: var(--body); }
.try    .blockLabel { color: var(--olive-2); }
.watch  { background: var(--panel); color: var(--body); }
.watch  .blockLabel { color: var(--olive); }
.track  { background: transparent; color: var(--body); }
.track  .blockLabel { color: var(--olive); }
.lookup { background: transparent; color: var(--body); border: 1px solid var(--rule); }
.lookup .blockLabel { color: var(--ink-55); }
.submit { background: var(--invert-bg); color: var(--invert-fg); }
.submit .blockLabel { color: var(--accent); }

/* Margin notes ----------------------------------------------------------- */
.marginNote { padding-left: 14px; border-left: 1px solid var(--gold-rule); }
.marginNote.boxed { padding: 14px; border-left: none; background: var(--panel); border-radius: var(--radius); }
.marginNote p { margin: 7px 0 0; font: 400 12.5px/1.6 var(--sans); color: var(--ink-72); }
.marginNote strong { font-weight: 700; color: var(--ink); }
.marginNote p.entry + p.entry { margin-top: 11px; }
/* Inline code at the sidebar's size, not the page's 14.5px, which towered over
   12.5px notes; and a tint that shows on the boxed panel, whose colour it shared. */
.marginNote .inlineCode { font-size: 11.5px; padding: 0 4px; background: var(--block-code); }

/* Buttons ---------------------------------------------------------------- */
.btn {
  border-radius: var(--radius);
  cursor: pointer;
  padding: 13px 24px;
  font: 600 12.5px var(--sans);
  letter-spacing: .04em;
  border: none;
  transition: background-color .15s ease, border-color .15s ease, color .15s ease;
}
.btn:disabled { cursor: default; opacity: .5; }
.btn.full { width: 100%; }

.primary { background: var(--ink); color: var(--paper); }
.primary:hover:not(:disabled) { background: color-mix(in srgb, var(--ink) 82%, var(--paper)); }

.secondary {
  background: transparent;
  color: var(--ink);
  border: 1px solid var(--ink-30);
  font-weight: 500;
  padding: 13px 20px;
}
.secondary:hover:not(:disabled) { border-color: var(--ink); }

.accent {
  background: var(--accent);
  color: #121212;
  font: 700 13px var(--sans);
  letter-spacing: .06em;
  text-transform: uppercase;
}
.accent:hover:not(:disabled) { background: var(--bright); }

.ghostDark {
  background: transparent;
  color: rgba(244, 244, 242, .75);
  border: 1px solid rgba(244, 244, 242, .22);
  font: 500 12px var(--sans);
  padding: 11px 18px;
}
.ghostDark:hover:not(:disabled) { border-color: rgba(244, 244, 242, .5); color: #f4f4f2; }

/* Meter ------------------------------------------------------------------ */
.meter { height: 4px; background: var(--ink-12); border-radius: 2px; overflow: hidden; }
.meter > span {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, var(--olive), var(--accent));
  transition: width .3s ease;
}

.stat dt { font: 400 10px var(--mono); letter-spacing: .14em; color: var(--ink-45); }
.stat dd { margin: 0; font: 300 22px var(--serif); color: var(--ink); }
